import { apiClient } from '@/services/api/client';

export interface WorkspaceOut {
  id: string;
  slug: string;
  name: string;
  plan: string;
  region: string;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceMembershipOut {
  workspace: WorkspaceOut;
  role: 'admin' | 'member' | 'viewer';
  accepted_at: string | null;
  last_active_at: string | null;
}

export interface WorkspaceMember {
  user_id: string;
  role: 'admin' | 'member' | 'viewer';
  invited_at: string;
  accepted_at: string | null;
  last_active_at: string | null;
}

export interface InviteOut {
  id: string;
  workspace_id: string;
  email: string;
  role: string;
  expires_at: string;
  sent_by_user_id: string;
}

export interface ApiTokenOut {
  id: string;
  workspace_id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  created_by_user_id: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

export interface ApiTokenCreatedOut extends ApiTokenOut {
  token: string;
}

export const workspacesApi = {
  list(): Promise<WorkspaceMembershipOut[]> {
    return apiClient.get('/v1/workspaces');
  },

  create(data: { slug: string; name: string }): Promise<WorkspaceOut> {
    return apiClient.post('/v1/workspaces', data);
  },

  update(workspaceId: string, data: { name: string }): Promise<WorkspaceOut> {
    return apiClient.patch(`/v1/workspaces/${workspaceId}`, data);
  },

  listMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    return apiClient.get(`/v1/workspaces/${workspaceId}/members`);
  },

  updateMemberRole(workspaceId: string, userId: string, role: string): Promise<{ ok: boolean }> {
    return apiClient.patch(`/v1/workspaces/${workspaceId}/members/${userId}`, { role });
  },

  removeMember(workspaceId: string, userId: string): Promise<void> {
    return apiClient.delete(`/v1/workspaces/${workspaceId}/members/${userId}`);
  },

  createInvite(workspaceId: string, data: { email: string; role: string }): Promise<InviteOut> {
    return apiClient.post(`/v1/workspaces/${workspaceId}/invites`, data);
  },

  acceptInvite(token: string): Promise<{ workspace_id: string }> {
    return apiClient.post('/v1/workspaces/invites/accept', { token });
  },

  listApiTokens(workspaceId: string): Promise<ApiTokenOut[]> {
    return apiClient.get(`/v1/workspaces/${workspaceId}/api-tokens`);
  },

  createApiToken(
    workspaceId: string,
    data: { name: string; scopes: string[]; expires_in_days?: number }
  ): Promise<ApiTokenCreatedOut> {
    return apiClient.post(`/v1/workspaces/${workspaceId}/api-tokens`, data);
  },

  revokeApiToken(workspaceId: string, tokenId: string): Promise<void> {
    return apiClient.delete(`/v1/workspaces/${workspaceId}/api-tokens/${tokenId}`);
  },
};
